import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { isNil } from 'lodash';

import container from 'src/ServiceContainer';
import { AppState, AppThunkDispatch } from 'src/store';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { makePopoverSensitive } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { ASSORTMENT_BUILD_FILTER_ALL_WARNING } from 'src/utils/Domain/Constants';
import { isViewDefnLoaded } from 'src/dao/tenantConfigClient';
import { ListDataOptions, TreePivot } from 'src/worker/pivotWorker.types';

import Subheader from 'src/components/Subheader/Subheader.container';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { getHeaderTextFromState } from 'src/components/ExtendedDataGrid/ExtendedDataGrid';
import { ConfigureOvertimeConfig } from 'src/pages/AssortmentBuild/OvertimeView/OvertimeView.types';
import { OvertimeView } from 'src/pages/AssortmentBuild/OvertimeView/OvertimeView';
import {
  setConfigureSelections,
  receiveTenantConfig as receiveNestedTenantConfig,
  requestTenantConfig as requestNestedTenantConfig,
  receiveError,
  fetchNestedOvertimeData,
} from 'src/pages/AssortmentBuild/OvertimeView/NestedOvertime/NestedOvertime.slice';
import { NestedOvertimeDispatchProps } from 'src/pages/AssortmentBuild/OvertimeView/NestedOvertime/NestedOvertimeContainer';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import ConfigureModal, { ConfigureModalProps, Option } from 'src/components/Configure/ConfigureModal';
import { listPairStyle } from 'src/components/ConfigurableGrid/ConfigurableGrid.styles';
import { AppType } from 'configuration-schemas/general';
import { WorklistNestedOvertimeOwnProps } from 'configuration-schemas/ownProps';

export function mapStateToPropsNestedOvertime(state: AppState, ownProps: WorklistNestedOvertimeOwnProps) {
  const customHeader = getHeaderTextFromState(state, true);
  const viewState = state.pages.hindsighting.nestedOvertime;
  const {
    viewDataState,
    viewDefnState,
    data,
    viewDefn,
    configureSelections,
    defaultConfigureSelections,
    configureOptions,
    rowHeight,
  } = viewState;
  const configLoaded = isViewDefnLoaded(viewDefnState);
  const dataLoaded = isDataLoaded(viewDataState);
  return {
    data,
    viewDefn,
    topMember: ownProps.topMembers,
    configureSelections,
    defaultConfigureSelections,
    configureOptions,
    configLoaded,
    dataLoaded,
    customHeader,
    rowHeight,
    viewDataState,
  };
}

export function dispatchToPropsNestedOvertime(
  dispatch: AppThunkDispatch,
  ownProps: WorklistNestedOvertimeOwnProps,
): NestedOvertimeDispatchProps {
  const { componentProps, topMembers } = ownProps;
  const client = container.tenantConfigClient;

  const dispatchHandlers = {
    async onShowView() {
      dispatch(requestNestedTenantConfig());
      await client
        .getTenantViewDefn<ConfigureOvertimeConfig>({
          defnId: componentProps.defns.view[0],
          appName: AppType.Assortment,
        })
        .then((resp) => {
          const defaultConfigureSelections = resp.configure.defaults.map((key, ind) => {
            const options = resp.configure.view[ind].options;
            return options.find((i) => i.dataIndex === key) || options[0];
          });
          const configureOptions = resp.configure.view;
          const configureSelections = resp.configure.defaults.map((key, ind) => {
            const options = resp.configure.view[ind].options;
            return options.find((i) => i.dataIndex === key) || options[0];
          });

          const rowHeight = resp.main && resp.main.rowHeight ? resp.main.rowHeight : 30;
          dispatch(
            receiveNestedTenantConfig({
              defaultConfigureSelections,
              configureOptions,
              configureSelections,
              viewDefn: resp,
              rowHeight,
              topMember: topMembers,
            }),
          );
        })
        .catch((error) => {
          receiveError({
            type: ComponentErrorType.config,
            message: (error as Error)?.message,
            name: ConfDefnComponentType.nestedOvertime,
            issues: error,
            defnId: error.defnId,
          });
        });
    },
    updateConfigureSelections(uncommittedSelections: Option[]) {
      dispatch(setConfigureSelections(uncommittedSelections));
    },
    onRefresh(_id: string) {
      const dataApi = componentProps.dataApi;
      const defn = dataApi?.params?.defnId;
      const options: ListDataOptions = {
        ...dataApi?.params,
        topMembers,
      };
      dispatch(fetchNestedOvertimeData(defn, options));
    },
    onError(error: any) {
      dispatch(
        receiveError({
          type: ComponentErrorType.config,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.nestedOvertime,
        }),
      );
    },
  };

  return dispatchHandlers;
}

interface NestedOvertimeGridWithSubheaderProps
  extends WorklistNestedOvertimeOwnProps,
    ReturnType<typeof mapStateToPropsNestedOvertime>,
    ReturnType<typeof dispatchToPropsNestedOvertime> {
  subheaderSummary?: string;
  overtimeData?: TreePivot;
}

const NestedOvertimeGridWithSubheader = ({ subheaderSummary, ...props }: NestedOvertimeGridWithSubheaderProps) => {
  const {
    viewDefn,
    rowHeight,
    defaultConfigureSelections,
    configureSelections,
    configureOptions,
    updateConfigureSelections,
    data,
    configLoaded,
    customHeader,
    topMember,
    onShowView,
    onRefresh,
    viewDataState,
  } = props;

  const [search, setSearch] = useState('');
  const [uncommittedConfigureSelections, setUncommittedConfigureSelections] = useState([] as Option[]);
  const [configureIsOpen, setConfigureIsOpen] = useState(false);

  useEffect(() => {
    onShowView();
  }, []);

  useEffect(() => {
    onRefresh('');
    // we only want to trigger this when the item changes, onRefresh is changed every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topMember]);

  const renderConfigureModal = () => {
    const configureModalProps: ConfigureModalProps = {
      enabled: true,
      isOpen: configureIsOpen,
      optionGroups: configureOptions || [],
      selections: uncommittedConfigureSelections || [],
      instructions: 'Select Aggregation Levels',
      onReset: () => {
        setUncommittedConfigureSelections(defaultConfigureSelections);
      },
      onToggleModal: (action) => {
        switch (action) {
          case 'apply': {
            if (uncommittedConfigureSelections) {
              setConfigureIsOpen(false);
              updateConfigureSelections(uncommittedConfigureSelections);
            }
            break;
          }
          default: {
            setConfigureIsOpen(false);
            setUncommittedConfigureSelections(configureSelections);
          }
        }
        // TODO: There has to be a better way... :/
        setConfigureIsOpen(!configureIsOpen);
      },
      selectionUpdate: (selections: Option[]) => {
        setUncommittedConfigureSelections(selections);
      },
    };
    return <ConfigureModal {...configureModalProps} />;
  };

  return (
    <div className={listPairStyle}>
      <Subheader
        title={''}
        showSearch={false}
        showFlowStatus={false}
        errorCondition={ASSORTMENT_BUILD_FILTER_ALL_WARNING}
        summary={subheaderSummary}
        configureOptions={{
          type: 'enabled',
          onConfigureClick: () => {
            setConfigureIsOpen(true);
            setUncommittedConfigureSelections(configureSelections);
          },
        }}
        searchReturn={(newVal: string) => {
          setSearch(newVal);
        }}
        viewDataState={viewDataState}
      />
      <div className={'data-container'}>
        <React.Fragment>
          {!isNil(data) && !isNil(viewDefn) ? (
            <OvertimeView
              loaded={configLoaded}
              viewConfig={viewDefn}
              rowHeight={rowHeight}
              aggBys={(configureSelections || []).map((sel) => sel.dataIndex)}
              treeData={data || []}
              search={search}
              exportOptions={{
                fileName: 'Nested Over Time',
                customHeader: customHeader,
              }}
            />
          ) : (
            <div />
          )}
        </React.Fragment>
        {renderConfigureModal()}
      </div>
    </div>
  );
};

export const TabbedNestedOvertimeGrid = connect(
  mapStateToPropsNestedOvertime,
  dispatchToPropsNestedOvertime,
  // @ts-ignore
)(makeScopeAndFilterSensitive(makePopoverSensitive(NestedOvertimeGridWithSubheader)));
